import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  X,
  Save,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

import { payrollAPI } from "@/lib/api";

export default function EditPayslipModal({ payslip, onClose, onUpdated }) {
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    statut: "no_change",
    salaire_net: "",
  });

  useEffect(() => {
    if (payslip) {
      setFormData({
        statut: "no_change",
        salaire_net: payslip.salaire_net
          ? parseFloat(payslip.salaire_net).toFixed(2)
          : "",
      });
    }
  }, [payslip]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getStatusInfo = (status) => {
    // Normalize the status value to handle potential variations
    const normalizedStatus = status?.toLowerCase()?.trim();

    const statusConfig = {
      brouillon: {
        label: "Brouillon",
        color: "bg-gray-100 text-gray-800",
        icon: Clock,
        description: "En cours de préparation",
      },
      valide: {
        label: "Validé",
        color: "bg-green-100 text-green-800",
        icon: CheckCircle,
        description: "Approuvé et prêt pour paiement",
      },
      paye: {
        label: "Payé",
        color: "bg-blue-100 text-blue-800",
        icon: DollarSign,
        description: "Paiement effectué",
      },
    };
    return statusConfig[normalizedStatus] || statusConfig.brouillon;
  };

  const getValidStatusTransitions = (currentStatus) => {
    // Normalize the status value to handle potential variations
    const normalizedStatus = currentStatus?.toLowerCase()?.trim();

    const transitions = {
      brouillon: ["valide"],
      valide: ["paye", "brouillon"],
      paye: ["valide"], // Can go back to validated but not draft
    };

    return transitions[normalizedStatus] || [];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {};
      if (
        formData.statut &&
        formData.statut !== "no_change" &&
        formData.statut !== payslip.statut
      ) {
        updateData.statut = formData.statut;
      }
      if (
        formData.salaire_net &&
        parseFloat(formData.salaire_net) !==
          parseFloat(payslip.salaire_net || 0)
      ) {
        updateData.salaire_net = parseFloat(formData.salaire_net);
      }

      if (Object.keys(updateData).length === 0) {
        showError("Aucune modification détectée");
        return;
      }

      const response = await payrollAPI.updatePayslip(payslip.id, updateData);

      showSuccess("Bulletin de paie mis à jour avec succès");
      onUpdated(response.data.data);
      onClose();
    } catch (error) {
      console.error("Error updating payslip:", error);
      const errorMessage =
        error.response?.data?.error || "Erreur lors de la mise à jour";
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!payslip) return null;

  const currentStatusInfo = getStatusInfo(payslip.statut);
  const validTransitions = getValidStatusTransitions(payslip.statut);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-sm font-medium text-blue-600">
                {payslip.employe?.prenom?.[0]}
                {payslip.employe?.nom?.[0]}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Modifier le Bulletin de Paie
              </h2>
              <p className="text-gray-600">
                {payslip.employe?.prenom} {payslip.employe?.nom} -{" "}
                {payslip.employe?.numero_employe}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Current Status Display */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <currentStatusInfo.icon className="h-5 w-5" />
                <span>Statut Actuel</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-3">
                <Badge className={currentStatusInfo.color}>
                  {currentStatusInfo.label}
                </Badge>
                <span className="text-sm text-gray-600">
                  {currentStatusInfo.description}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Edit Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Status Update */}
            <div className="space-y-2">
              <Label htmlFor="statut">
                Nouveau Statut
                <span className="text-gray-500 ml-1">(optionnel)</span>
              </Label>
              <Select
                value={formData.statut}
                onValueChange={(value) => handleInputChange("statut", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Conserver le statut actuel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_change">
                    Conserver le statut actuel
                  </SelectItem>
                  {validTransitions.map((status) => {
                    const statusInfo = getStatusInfo(status);
                    return (
                      <SelectItem key={status} value={status}>
                        <div className="flex items-center space-x-2">
                          <statusInfo.icon className="h-4 w-4" />
                          <span>{statusInfo.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {validTransitions.length === 0 && (
                <p className="text-sm text-amber-600 flex items-center space-x-1">
                  <AlertCircle className="h-4 w-4" />
                  <span>Aucune transition de statut disponible</span>
                </p>
              )}
            </div>

            {/* Net Salary Update */}
            <div className="space-y-2">
              <Label htmlFor="salaire_net">
                Salaire Net (MAD)
                <span className="text-gray-500 ml-1">(optionnel)</span>
              </Label>
              <div className="relative">
                <Input
                  id="salaire_net"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.salaire_net}
                  onChange={(e) =>
                    handleInputChange("salaire_net", e.target.value)
                  }
                  placeholder="Laisser vide pour conserver la valeur actuelle"
                  className="pl-12 pr-4"
                />
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium text-sm">
                  MAD
                </span>
              </div>
              <p className="text-sm text-gray-500">
                Valeur actuelle:{" "}
                {parseFloat(payslip.salaire_net || 0).toLocaleString("fr-MA", {
                  style: "currency",
                  currency: "MAD",
                })}
              </p>
            </div>

            {/* Summary of Changes */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <h4 className="font-medium text-blue-900 mb-2">
                  Résumé des modifications
                </h4>
                <div className="space-y-2 text-sm text-blue-800">
                  {formData.statut &&
                    formData.statut !== "no_change" &&
                    formData.statut !== payslip.statut && (
                      <div className="flex items-center space-x-2">
                        <span>• Statut:</span>
                        <Badge variant="outline" className="text-blue-700">
                          {payslip.statut} → {formData.statut}
                        </Badge>
                      </div>
                    )}
                  {formData.salaire_net &&
                    parseFloat(formData.salaire_net) !==
                      parseFloat(payslip.salaire_net || 0) && (
                      <div className="flex items-center space-x-2">
                        <span>• Salaire Net:</span>
                        <span>
                          {parseFloat(payslip.salaire_net || 0).toLocaleString(
                            "fr-MA",
                            {
                              style: "currency",
                              currency: "MAD",
                            }
                          )}{" "}
                          →{" "}
                          {parseFloat(formData.salaire_net).toLocaleString(
                            "fr-MA",
                            {
                              style: "currency",
                              currency: "MAD",
                            }
                          )}
                        </span>
                      </div>
                    )}

                  {Object.keys(formData).every(
                    (key) =>
                      (key === "statut" && formData[key] === "no_change") ||
                      (key === "salaire_net" &&
                        parseFloat(formData[key] || 0) ===
                          parseFloat(payslip[key] || 0))
                  ) && (
                    <div className="text-blue-600 italic">
                      Aucune modification détectée
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              loading ||
              (formData.statut === "no_change" &&
                (!formData.salaire_net ||
                  parseFloat(formData.salaire_net) ===
                    parseFloat(payslip.salaire_net || 0)))
            }
            className="flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>{loading ? "Mise à jour..." : "Mettre à jour"}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
